import React, { useEffect, useRef } from 'react';

import snsWebSdk, { SnsWebSdkBaseConfig } from '@sumsub/websdk';
import { WebSDKEvents } from '@sumsub/websdk/types/types';

import { bemCssModules, getTestId, handleLocalStorage } from '@utils';
import { useNavigate } from '@utils/hooks';
import i18n from '@utils/i18n';

import { useStore, userActions } from '@modules';
import { VerificationStatusesEnum } from '@modules/user/types';

import style from './Sumsub.scss';

const ti = getTestId('Sumsub');
const cn = bemCssModules(style, 'Sumsub');
const { setItem } = handleLocalStorage;

export type SumsubType = {
  isModal?: boolean;
  isKYC2?: boolean;
  onClose?(): void;
};

export type SumsubIdCheckType = {
  applicantStatus: WebSDKEvents['idCheck.applicantStatus'] & {
    reviewResult?: { reviewAnswer?: 'GREEN' | 'RED'; reviewRejectType: 'RETRY' };
  };
  stepInitiated: WebSDKEvents['idCheck.onStepInitiated'];
  stepCompleted: WebSDKEvents['idCheck.stepCompleted'];
};

const Sumsub: CustomFC<SumsubType> = ({ isModal, isKYC2, onClose, className }) => {
  const [{ user, global }, dispatch] = useStore();
  const navigate = useNavigate();
  const timeout = useRef<NodeJS.Timeout>(null);
  const { complete } = VerificationStatusesEnum;
  const webSDKEl = useRef<HTMLDivElement>(null);
  const { data, KYC } = user;
  const { countries, scheme } = global;
  const countryCode = data?.country_code;
  const countryCode3 = countries.find(({ code }) => countryCode === code)?.code3;

  const isKYC1Complete = data?.checks.document === complete;

  const handleClose = () => {
    if (isModal) dispatch(userActions.skipKYC());
    if (isKYC2) {
      if (typeof onClose === 'function') onClose();
      dispatch(userActions.setIbanStatus());
      navigate(CONST.nav.accountsVerification);
    } else navigate(CONST.nav.root);
  };

  const updateKYCToken = async () => {
    return dispatch(userActions.accessKYCToken())
      .unwrap()
      .then(response => response.data.token);
  };

  useEffect(() => {
    if (!isKYC1Complete) dispatch(userActions.accessKYCToken());
    else if (isKYC2) dispatch(userActions.accessKYCToken({ kyc2: isKYC2 }));
    else handleClose();

    return () => {
      dispatch(userActions.clearKYCToken());
      if (timeout.current) clearTimeout(timeout.current);
      dispatch(userActions.getUserData());
    };
  }, []);

  const applicantStatus = (payload: SumsubIdCheckType['applicantStatus']) => {
    const { reviewResult, reviewStatus } = payload;
    const reviewRejectType = reviewResult?.reviewRejectType || '';

    if (reviewRejectType !== 'RETRY' || reviewStatus === 'pending') {
      dispatch(userActions.getUserData());
      timeout.current = setTimeout(handleClose, CONST.KYCRedirectTimeout);
    }
  };

  const onApplicantSubmitted = () => {
    setItem('KYCStarted', true);
  };

  useEffect(() => {
    if (KYC?.token && webSDKEl.current && scheme) {
      const country = countryCode3?.toUpperCase() || CONST.defaultCountryCode.toUpperCase();
      const snsWebSdkInstance = snsWebSdk
        .init(KYC.token, updateKYCToken)
        .withBaseUrl(CONST.sumsubApiUrl)
        .withConf({
          lang: i18n.language,
          country,
          version: 2,
          theme: scheme,
        } as SnsWebSdkBaseConfig)
        .on('idCheck.onApplicantStatusChanged', applicantStatus)
        .on('idCheck.onApplicantSubmitted', onApplicantSubmitted)
        .build();
      snsWebSdkInstance.launch(webSDKEl.current);
    }
  }, [KYC?.token, scheme]);

  return <div ref={webSDKEl} className={cn(null, [className])} data-testid={ti()} />;
};

export default Sumsub;
